import type { QueryClient, QueryKey } from '@tanstack/react-query'

/**
 * Invalidate a list query after a create/update/delete mutation resolves,
 * without losing the race against that list query's OWN in-flight fetch.
 *
 * ## The bug this guards against
 *
 * `queryClient.invalidateQueries()` only cancels-and-restarts an in-flight
 * fetch when the query already holds data — see TanStack Query's
 * `Query#fetch` (query-core), which gates its cancel-and-refetch branch on
 * `state.data !== undefined`:
 *
 * ```
 * if (this.state.data !== void 0 && fetchOptions?.cancelRefetch) {
 *   this.cancel({ silent: true })          // → starts a brand-new fetch
 * } else if (this.#retryer) {
 *   this.#retryer.continueRetry()
 *   return this.#retryer.promise          // → reuses the IN-FLIGHT fetch, no new request
 * }
 * ```
 *
 * When a section's list query has never resolved data yet — its very first
 * load is still in flight — and a create/update/delete mutation for that
 * same list succeeds *during* that window (e.g. the user opens a tab and
 * submits the "Add X" modal before the tab's initial GET has landed),
 * `invalidateQueries()` silently reuses that pending, pre-mutation fetch's
 * promise instead of issuing a new request. That fetch was started BEFORE
 * the mutation, so once it resolves the query settles into a state that
 * looks perfectly fresh (`isInvalidated: false`, real `dataUpdatedAt`) while
 * actually holding pre-mutation rows — with nothing left to prompt a real
 * refetch. The success toast fires, but the list doesn't update until some
 * unrelated later action (a reload, a second create, a refocus) happens to
 * trigger a genuinely new fetch. This is exactly the "toast fires, list
 * doesn't update, only a reload/second action reveals the new row"
 * intermittent bug reported across the entity-detail create flows.
 *
 * Verified experimentally against the installed @tanstack/react-query
 * (5.90.x): a bare `invalidateQueries()` call issued while the list's first
 * load is in flight resolves to the pre-mutation data and 0 additional
 * queryFn calls; `cancelQueries()` followed by `invalidateQueries()` always
 * triggers a fresh queryFn call and resolves to the post-mutation data.
 *
 * ## The fix
 *
 * `cancelQueries()` cancels any outstanding fetch — regardless of whether
 * that query ever resolved data before — resetting its `fetchStatus` back to
 * `'idle'`. The stale in-flight request is not aborted at the network layer
 * (our GraphQL fetchers don't wire up the AbortSignal), but TanStack Query's
 * retryer guards against a late resolution of a cancelled fetch ever being
 * committed to the query's state, so it's safe to let it keep running in the
 * background. With `fetchStatus` back to `'idle'`, the subsequent
 * `invalidateQueries()` call always takes the "start a new fetch" path.
 */
export async function invalidateListQuery(queryClient: QueryClient, queryKey: QueryKey): Promise<void> {
  await queryClient.cancelQueries({ queryKey })
  await queryClient.invalidateQueries({ queryKey })
}
